import { API_BASE_URL } from '../config/api';
import { 
  TrackReport, 
  FeedbackReport, 
  ReportStatsResponse, 
  ResolveReportRequest,
  PagedResult 
} from '../types/artist';

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

export const adminApi = {
  // Get report statistics
  async getReportStats(): Promise<ReportStatsResponse> {
    try {
      console.log('Getting report stats');
      console.log('API URL:', `${API_BASE_URL}/api/Admin/reports/stats`);
      
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/api/Admin/reports/stats`, {
        method: 'GET',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('Get report stats response status:', response.status);

      let data;
      try {
        data = await response.json();
        console.log('Get report stats response data:', data);
      } catch (jsonError) {
        console.error('Failed to parse JSON response:', jsonError);
        throw new ApiError(response.status, 'Invalid response format from server');
      }

      if (!response.ok) {
        console.error('Get report stats failed with status:', response.status);
        throw new ApiError(response.status, data.message || 'Failed to get report stats');
      }

      return data;
    } catch (error) {
      console.error('Get report stats error:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(0, 'Network error occurred: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  },

  // Get track reports with pagination
  async getTrackReports(page: number = 1, pageSize: number = 20, resolved?: boolean): Promise<PagedResult<TrackReport>> {
    try {
      console.log('Getting track reports, page:', page, 'pageSize:', pageSize, 'resolved:', resolved);
      
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('pageSize', pageSize.toString());
      if (resolved !== undefined) {
        params.append('resolved', resolved.toString());
      }
      
      const url = `${API_BASE_URL}/api/Admin/reports/tracks?${params.toString()}`;
      console.log('API URL:', url);
      
      const token = localStorage.getItem('accessToken');
      const response = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('Get track reports response status:', response.status);

      let data;
      try {
        data = await response.json();
        console.log('Get track reports response data:', data);
      } catch (jsonError) {
        console.error('Failed to parse JSON response:', jsonError);
        throw new ApiError(response.status, 'Invalid response format from server');
      }

      if (!response.ok) {
        console.error('Get track reports failed with status:', response.status);
        throw new ApiError(response.status, data.message || 'Failed to get track reports');
      }

      return data;
    } catch (error) {
      console.error('Get track reports error:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(0, 'Network error occurred: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  },

  // Get feedback reports with pagination
  async getFeedbackReports(page: number = 1, pageSize: number = 20, resolved?: boolean): Promise<PagedResult<FeedbackReport>> {
    try {
      console.log('Getting feedback reports, page:', page, 'pageSize:', pageSize, 'resolved:', resolved);
      
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('pageSize', pageSize.toString());
      if (resolved !== undefined) {
        params.append('resolved', resolved.toString());
      }
      
      const url = `${API_BASE_URL}/api/Admin/reports/feedback?${params.toString()}`;
      console.log('API URL:', url);
      
      const token = localStorage.getItem('accessToken');
      const response = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('Get feedback reports response status:', response.status);

      let data;
      try {
        data = await response.json();
        console.log('Get feedback reports response data:', data);
      } catch (jsonError) {
        console.error('Failed to parse JSON response:', jsonError);
        throw new ApiError(response.status, 'Invalid response format from server');
      }

      if (!response.ok) {
        console.error('Get feedback reports failed with status:', response.status);
        throw new ApiError(response.status, data.message || 'Failed to get feedback reports');
      }

      return data;
    } catch (error) {
      console.error('Get feedback reports error:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(0, 'Network error occurred: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  },

  // Resolve a track report
  async resolveTrackReport(reportId: number, resolution: ResolveReportRequest): Promise<void> {
    try {
      console.log('Resolving track report:', reportId);
      console.log('API URL:', `${API_BASE_URL}/api/Admin/reports/tracks/${reportId}/resolve`);
      
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/api/Admin/reports/tracks/${reportId}/resolve`, {
        method: 'PUT',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(resolution),
      });

      console.log('Resolve track report response status:', response.status);

      if (!response.ok) {
        let errorMessage = 'Failed to resolve track report';
        try {
          const data = await response.json();
          errorMessage = data.message || errorMessage;
        } catch (jsonError) {
          // If we can't parse JSON, use default message
        }
        console.error('Resolve track report failed with status:', response.status);
        throw new ApiError(response.status, errorMessage);
      }
    } catch (error) {
      console.error('Resolve track report error:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(0, 'Network error occurred: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  },

  // Resolve a feedback report
  async resolveFeedbackReport(reportId: number, resolution: ResolveReportRequest): Promise<void> {
    try {
      console.log('Resolving feedback report:', reportId);
      console.log('API URL:', `${API_BASE_URL}/api/Admin/reports/feedback/${reportId}/resolve`);
      
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/api/Admin/reports/feedback/${reportId}/resolve`, {
        method: 'PUT',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(resolution),
      });

      console.log('Resolve feedback report response status:', response.status);

      if (!response.ok) {
        let errorMessage = 'Failed to resolve feedback report';
        try {
          const data = await response.json();
          errorMessage = data.message || errorMessage;
        } catch (jsonError) {
          // If we can't parse JSON, use default message
        }
        console.error('Resolve feedback report failed with status:', response.status);
        throw new ApiError(response.status, errorMessage);
      }
    } catch (error) {
      console.error('Resolve feedback report error:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(0, 'Network error occurred: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  },

  // Delete a track report
  async deleteTrackReport(reportId: number): Promise<void> {
    try {
      console.log('Deleting track report:', reportId);
      console.log('API URL:', `${API_BASE_URL}/api/Admin/reports/tracks/${reportId}`);
      
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/api/Admin/reports/tracks/${reportId}`, {
        method: 'DELETE',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('Delete track report response status:', response.status);

      if (!response.ok) {
        let errorMessage = 'Failed to delete track report';
        try {
          const data = await response.json();
          errorMessage = data.message || errorMessage;
        } catch (jsonError) {
          // If we can't parse JSON, use default message
        }
        console.error('Delete track report failed with status:', response.status);
        throw new ApiError(response.status, errorMessage);
      }
    } catch (error) {
      console.error('Delete track report error:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(0, 'Network error occurred: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  },

  // Delete a feedback report
  async deleteFeedbackReport(reportId: number): Promise<void> {
    try {
      console.log('Deleting feedback report:', reportId);
      console.log('API URL:', `${API_BASE_URL}/api/Admin/reports/feedback/${reportId}`);
      
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/api/Admin/reports/feedback/${reportId}`, {
        method: 'DELETE',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('Delete feedback report response status:', response.status);

      if (!response.ok) {
        let errorMessage = 'Failed to delete feedback report';
        try {
          const data = await response.json();
          errorMessage = data.message || errorMessage;
        } catch (jsonError) {
          // If we can't parse JSON, use default message
        }
        console.error('Delete feedback report failed with status:', response.status);
        throw new ApiError(response.status, errorMessage);
      }
    } catch (error) {
      console.error('Delete feedback report error:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(0, 'Network error occurred: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  },
};
